import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, AppBar, Toolbar, Typography, IconButton, Box, TextField, InputAdornment,
  Chip, Card, CardActionArea, CardContent, CircularProgress, Stack, Tooltip,
  useTheme, useMediaQuery, Slide
} from '@mui/material';
import {
  Close as CloseIcon, Search as SearchIcon, Chat as ChatIcon,
  AutoStories as StoriesIcon, History as HistoryIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Friendly "2 days ago" style label for the last-used timestamp.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  const diff = Math.max(0, Date.now() - then.getTime());
  const day = 24 * 60 * 60 * 1000;
  if (diff < 60 * 1000) return 'just now';
  if (diff < 60 * 60 * 1000) return `${Math.round(diff / (60 * 1000))}m ago`;
  if (diff < day) return `${Math.round(diff / (60 * 60 * 1000))}h ago`;
  if (diff < 7 * day) return `${Math.round(diff / day)}d ago`;
  return then.toLocaleDateString();
};

/**
 * Full-screen (mobile) / large (desktop) modal that browses the shared idea or
 * prompt library. Pick an item and it's handed back via onSelect — the parent
 * decides what to do with it (prefill a box, etc.). Usage is recorded here so
 * "last used" badges stay accurate no matter who opens the browser.
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   kind       'discover' | 'journal'
 *   onSelect   (item) => void
 *   heading    optional title text
 */
const LibraryBrowser = ({ open, onClose, kind, onSelect, heading }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiClient.library.list(kind);
        if (cancelled) return;
        setItems(res.data.items || []);
        setCategories(res.data.categories || []);
        setActiveCategory('All');
        setSearch('');
      } catch (e) {
        console.error('Failed to load library', e);
        if (!cancelled) { setItems([]); setCategories([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, kind]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (activeCategory !== 'All' && it.category !== activeCategory) return false;
      if (!q) return true;
      const hay = `${it.title} ${it.description || ''} ${(it.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, activeCategory, search]);

  // Group the filtered items by category for section headers.
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category).push(it);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const handlePick = (item) => {
    onSelect && onSelect(item);
    // Fire-and-forget usage tracking; the badge updates on next open.
    apiClient.library.markUsed(item.id).catch(() => {});
    onClose && onClose();
  };

  const defaultHeading = kind === 'journal' ? 'Browse writing topics' : 'Browse ideas';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      TransitionComponent={fullScreen ? Transition : undefined}
      PaperProps={{ sx: { height: fullScreen ? '100%' : '85vh' } }}
    >
      <AppBar position="sticky" color="inherit" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flex: 1 }} noWrap>
            {heading || defaultHeading}
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Search + category filter */}
      <Box sx={{ p: 2, pb: 1, position: 'sticky', top: 64, bgcolor: 'background.paper', zIndex: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search ideas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {['All', ...categories].map((cat) => (
            <Chip
              key={cat}
              label={cat}
              onClick={() => setActiveCategory(cat)}
              color={activeCategory === cat ? 'primary' : 'default'}
              variant={activeCategory === cat ? 'filled' : 'outlined'}
              size="small"
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography variant="body1">No ideas match your search.</Typography>
          </Box>
        ) : (
          grouped.map(([category, list]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: 1, fontWeight: 700 }}
              >
                {category}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5,
                  mt: 0.5,
                }}
              >
                {list.map((item) => {
                  const usedLabel = item.last_used_at ? relativeTime(item.last_used_at) : null;
                  return (
                    <Card key={item.id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardActionArea onClick={() => handlePick(item)} sx={{ height: '100%' }}>
                        <CardContent sx={{ pb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            {item.emoji && (
                              <Typography sx={{ fontSize: 22, lineHeight: 1 }}>{item.emoji}</Typography>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {item.title}
                              </Typography>
                              {item.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {item.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, alignItems: 'center' }}>
                            {item.content_type && (
                              <Chip
                                size="small"
                                variant="outlined"
                                icon={item.content_type === 'conversation' ? <ChatIcon /> : <StoriesIcon />}
                                label={
                                  item.content_type === 'conversation' ? 'Conversation'
                                    : item.content_type === 'story' ? 'Story' : 'Story / Chat'
                                }
                              />
                            )}
                            {item.difficulty && (
                              <Chip size="small" variant="outlined" label={item.difficulty} />
                            )}
                            {usedLabel && (
                              <Tooltip title={`Used ${item.use_count} time${item.use_count === 1 ? '' : 's'}`}>
                                <Chip
                                  size="small"
                                  color="default"
                                  icon={<HistoryIcon />}
                                  label={`Used ${usedLabel}`}
                                  sx={{ ml: 'auto' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Dialog>
  );
};

export default LibraryBrowser;
